import type { Expand } from "@octopusdeploy/type-utils";
import { useCallback, useMemo } from "react";
import { useHistory, useLocation } from "react-router";
import URI from "urijs";
export interface QueryParam<TKey extends string, TValue> {
    name: TKey;
    parse: (serializedValue: string) => TValue | ParseError;
    serialize: (value: TValue) => string;
}
export interface ArrayQueryParam<TKey extends string, TValue> {
    name: TKey;
    parse: (serializedValue: string) => TValue | ParseError;
    serialize: (value: TValue) => string;
    isArray: true;
}
// This type is used for type constraints and cannot be "unknown"
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type UnknownQueryParam = QueryParam<string, any> | ArrayQueryParam<string, any>;
export const parseError = Symbol();
export type ParseError = typeof parseError;
export function createQueryParam<TKey extends string, TValue>(paramName: TKey, parse: (serializedValue: string) => TValue | ParseError, serialize: (value: TValue) => string, isArray?: boolean): QueryParam<TKey, TValue> {
    return {
        name: paramName,
        parse,
        serialize,
    };
}
export function createArrayQueryParam<TKey extends string, TValue>(paramName: TKey, parse: (serializedValue: string) => TValue | ParseError, serialize: (value: TValue) => string): ArrayQueryParam<TKey, TValue> {
    return {
        name: paramName,
        parse,
        serialize,
        isArray: true,
    };
}
export function stringQueryParam<TKey extends string>(paramName: TKey): QueryParam<TKey, string> {
    return createQueryParam(paramName, (serializedValue) => serializedValue, (value) => value);
}
export function stringArrayQueryParam<TKey extends string>(paramName: TKey): ArrayQueryParam<TKey, string> {
    return createArrayQueryParam(paramName, (serializedValue) => serializedValue, (value) => value);
}
export function numberQueryParam<TKey extends string>(paramName: TKey): QueryParam<TKey, number> {
    return createQueryParam(paramName, (serializedValue) => {
        const parsedValue = parseInt(serializedValue);
        if (Number.isNaN(parsedValue))
            return parseError;
        return parsedValue;
    }, (value) => value.toString());
}
export function booleanQueryParam<TKey extends string>(paramName: TKey): QueryParam<TKey, boolean> {
    return createQueryParam(paramName, (serializedValue) => (serializedValue === "true" ? true : serializedValue === "false" ? false : parseError), (value) => value.toString());
}
export function dateQueryParam<TKey extends string>(paramName: TKey): QueryParam<TKey, Date> {
    return createQueryParam(paramName, (serializedValue) => {
        const parsedDate = new Date(serializedValue);
        if (Number.isNaN(parsedDate.valueOf())) {
            return parseError;
        }
        return parsedDate;
    }, (value) => value.toISOString());
}
export type ParsedQueryParams<QueryParams> = QueryParams extends UnknownQueryParam[] ? ParseQueryParamsArray<QueryParams> : never;
type ParseQueryParamsArray<QueryParams extends UnknownQueryParam[]> = Expand<MergeObjects<{
    [K in keyof QueryParams]: MapQueryParamToObject<QueryParams[K]>;
}>>;
type MapQueryParamToObject<Param extends UnknownQueryParam> = Param extends ArrayQueryParam<infer TKey extends string, infer TValue> ? QueryStringProperties<TKey, TValue[]> : Param extends QueryParam<infer TKey extends string, infer TValue> ? QueryStringProperties<TKey, TValue> : never;
type MapQueryParamToValue<Param extends UnknownQueryParam> = Param extends ArrayQueryParam<infer TKey extends string, infer TValue> ? TValue[] : Param extends QueryParam<infer TKey extends string, infer TValue> ? TValue : never;
type QueryStringProperties<TKey extends string, TValue> = {
    [N in TKey]?: TValue;
};
type MergeObjects<T extends unknown[]> = T extends [
    infer Value
] ? Value : T extends [
    infer Value,
    ...infer Rest
] ? Value & MergeObjects<Rest> : {};
type NextQueryParamValue<T> = T | ((prevValue: T) => T);
export type SetQueryParamValue<T> = (value: NextQueryParamValue<T>) => void;
export enum QueryStateMode {
    PushHistory = "PushHistory",
    ReplaceHistory = "ReplaceHistory"
}
export function useQueryStringParams<QueryStringParams extends UnknownQueryParam[]>(paramDefinitions: [
    ...QueryStringParams
], mode: QueryStateMode = QueryStateMode.PushHistory): [
    ParsedQueryParams<QueryStringParams>,
    SetQueryParamValue<ParsedQueryParams<QueryStringParams>>
] {
    const updateUrl = useUpdateUrl();
    const { search, pathname } = useLocation();
    const values = useMemo(() => {
        const params = new URLSearchParams(search);
        const values = paramDefinitions.reduce<Partial<ParsedQueryParams<QueryStringParams>>>((prev, paramDefinition) => {
            const name = paramDefinition.name;
            const parsedValue = parseQueryParameterValueFromUrl(params, paramDefinition);
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            prev[name as keyof ParsedQueryParams<QueryStringParams>] = parsedValue;
            return prev;
        }, {});
        // We know this is a complete (non-partial) object at this point
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        return values as ParsedQueryParams<QueryStringParams>;
    }, [paramDefinitions, search]);
    const updateQueryParamValue = useCallback((nextQueryParamValue: NextQueryParamValue<ParsedQueryParams<QueryStringParams>>) => {
        const url = new URI(pathname + search);
        const newParamValue = isQueryParamValueFunction(nextQueryParamValue) ? nextQueryParamValue(values) : nextQueryParamValue;
        const serializedQueryParams = serializeQueryParams(paramDefinitions, newParamValue);
        serializedQueryParams.forEach(({ name, values }) => {
            if (values !== undefined) {
                url.setQuery(name, values);
            }
            else {
                url.removeQuery(name);
            }
        });
        updateUrl(url, mode);
    }, [paramDefinitions, pathname, updateUrl, search]);
    return [values, updateQueryParamValue];
}
export function useQueryStringParam<Param extends UnknownQueryParam>(paramDefinition: Param, mode: QueryStateMode = QueryStateMode.PushHistory): [
    MapQueryParamToValue<Param> | undefined,
    SetQueryParamValue<MapQueryParamToValue<Param> | undefined>
] {
    const updateUrl = useUpdateUrl();
    const { search, pathname } = useLocation();
    const params = new URLSearchParams(search);
    const serializedValues = params.getAll(paramDefinition.name).sort();
    const parsedValue = parseQueryParameterValueFromUrl(params, paramDefinition);
    const updateQueryParamValue = useCallback((nextQueryParamValue: NextQueryParamValue<MapQueryParamToValue<typeof paramDefinition> | undefined>) => {
        const url = new URI(pathname + search);
        const newParamValue = isQueryParamValueFunction(nextQueryParamValue) ? nextQueryParamValue(parsedValue) : nextQueryParamValue;
        if (newParamValue === undefined) {
            url.removeQuery(paramDefinition.name);
            updateUrl(url, mode);
        }
        else {
            const newSerializedValues = serializeQueryParam(paramDefinition, newParamValue)?.sort() ?? [];
            if (!arrayShallowEqual(serializedValues, newSerializedValues)) {
                if (newSerializedValues.length > 0) {
                    url.setQuery(paramDefinition.name, newSerializedValues);
                }
                else {
                    url.removeQuery(paramDefinition.name);
                }
                updateUrl(url, mode);
            }
        }
    }, [mode, paramDefinition, pathname, updateUrl, search, serializedValues]);
    return [parsedValue, updateQueryParamValue];
}
function arrayShallowEqual<T>(array1: T[], array2: T[]) {
    if (array1.length !== array2.length) {
        return false;
    }
    return array1.every((value, index) => value === array2[index]);
}
interface SerializedQueryParameter {
    name: string;
    values: string[] | undefined;
}
export function serializeQueryParams<QueryParams extends UnknownQueryParam[]>(queryParams: [
    ...QueryParams
], newQueryValues: ParsedQueryParams<QueryParams>): SerializedQueryParameter[] {
    return queryParams.map((queryParam) => {
        const name = queryParam.name;
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        const newValue = newQueryValues[name as keyof ParsedQueryParams<QueryParams>];
        const serializedValues = serializeQueryParam(queryParam, newValue);
        return { name, values: serializedValues };
    });
}
function serializeQueryParam<TKey extends string, TValue>(queryParam: QueryParam<TKey, TValue> | ArrayQueryParam<TKey, TValue>, newValue: TValue | TValue[]): string[] | undefined {
    if (newValue === undefined) {
        return undefined;
    }
    if (isArrayQueryParam(queryParam) && Array.isArray(newValue)) {
        const serializedValues = newValue.map(queryParam.serialize);
        return serializedValues.length > 0 ? serializedValues : undefined;
    }
    if (!isArrayQueryParam(queryParam) && !Array.isArray(newValue)) {
        return [queryParam.serialize(newValue)];
    }
    return undefined;
}
function parseQueryParameterValueFromUrl<TKey extends string, TValue>(urlSearchParams: URLSearchParams, paramDefinition: QueryParam<TKey, TValue> | ArrayQueryParam<TKey, TValue>): TValue | TValue[] | undefined {
    const name = paramDefinition.name;
    if (isArrayQueryParam(paramDefinition)) {
        const paramStringValues = urlSearchParams.getAll(name);
        const parsedValues = paramStringValues.map(paramDefinition.parse).filter(isNotParseError);
        return parsedValues.length > 0 ? parsedValues : undefined;
    }
    else {
        const paramStringValue = urlSearchParams.get(name);
        if (paramStringValue !== null) {
            const parsedValue = paramDefinition.parse(paramStringValue);
            return isNotParseError(parsedValue) ? parsedValue : undefined;
        }
    }
    return undefined;
}
function isNotParseError<T>(value: T | ParseError): value is T {
    return value !== parseError;
}
function isArrayQueryParam<TKey extends string, TValue>(value: QueryParam<TKey, TValue> | ArrayQueryParam<TKey, TValue>): value is ArrayQueryParam<TKey, TValue> {
    return "isArray" in value && value.isArray;
}
function isQueryParamValueFunction<T>(value: NextQueryParamValue<T>): value is (t: T) => T {
    return typeof value === "function";
}
function useUpdateUrl() {
    const { push, replace } = useHistory();
    return (url: URI, mode: QueryStateMode = QueryStateMode.PushHistory) => {
        const newUrl = url.toString();
        if (mode === QueryStateMode.PushHistory) {
            push(newUrl);
        }
        else {
            replace(newUrl);
        }
    };
}
